import { Chip, EmptyState, ErrorBanner, Section, SkeletonRows } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Frame, PaneHeader } from '../../components/frame'
import { describeError } from '../../lib/api/client'
import { satisfactionReportQuery } from './api'

export function SatisfactionReport() {
  const query = useQuery(satisfactionReportQuery())
  return (
    <Frame
      active="satisfaction"
      railless
      main={
        <div className="flex h-full flex-col overflow-y-auto">
          <PaneHeader title="Satisfaction" subtitle="Customer ratings after a case closes." />
          <div className="max-w-5xl space-y-4 p-5">
            {query.isPending ? <SkeletonRows count={6} /> : null}
            {query.isError ? (
              <ErrorBanner title="Satisfaction is unreachable" detail={describeError(query.error)} onRetry={() => void query.refetch()} />
            ) : null}
            {query.data ? (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <Section title="Average">
                    <p className="text-2xl font-semibold">{query.data.summary.average_score?.toFixed(2) ?? '—'} / 3</p>
                  </Section>
                  <Section title="Response rate">
                    <p className="text-2xl font-semibold">{query.data.summary.response_rate_pct?.toFixed(1) ?? '—'}%</p>
                  </Section>
                  <Section title="Responses">
                    <p className="text-2xl font-semibold">{query.data.summary.responses}</p>
                  </Section>
                  <Section title="Sent">
                    <p className="text-2xl font-semibold">{query.data.summary.sent}</p>
                  </Section>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Chip tone="success">Good {query.data.summary.good}</Chip>
                  <Chip tone="warning">OK {query.data.summary.ok}</Chip>
                  <Chip tone="danger">Bad {query.data.summary.bad}</Chip>
                </div>
                {query.data.rows.length ? (
                  <Section title="Responses" count={query.data.rows.length}>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="text-xs text-muted-foreground">
                          <tr>
                            <th className="py-2 text-left">Case</th>
                            <th className="text-left">Customer</th>
                            <th className="text-left">Rating</th>
                            <th className="text-left">Comment</th>
                            <th className="text-left">Handled actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {query.data.rows.map((row) => (
                            <tr key={row.case_id} className="border-t border-border">
                              <td className="py-2">
                                <Link to="/case/$id" params={{ id: row.case_number }} className="font-medium text-primary">
                                  {row.case_number}
                                </Link>
                              </td>
                              <td>{row.customer ?? 'Unresolved customer'}</td>
                              <td>
                                <Chip tone={row.rating === 'good' ? 'success' : row.rating === 'bad' ? 'danger' : 'warning'}>
                                  {row.rating.toUpperCase()}
                                </Chip>
                              </td>
                              <td className="max-w-md">{row.comment ?? '—'}</td>
                              <td>{row.action_classes?.join(', ') || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Section>
                ) : (
                  <EmptyState title="Nothing matched" detail="No customer has submitted a Patchbay satisfaction rating yet." />
                )}
              </>
            ) : null}
          </div>
        </div>
      }
    />
  )
}
